import {
  Building2,
  CheckCircle2,
  ExternalLink,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Send,
  Sparkles,
} from 'lucide-react';
import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Button from '../../common/Button';
import Container from '../../common/Container';
import ScrollReveal from '../common/ScrollReveal';

interface FormData {
  name: string;
  hospital: string;
  email: string;
  phone: string;
  module: string;
  message: string;
}

const initialForm: FormData = {
  name: '',
  hospital: '',
  email: '',
  phone: '',
  module: 'All Modules (Complete ERP)',
  message: '',
};

export const Contact = () => {
  const location = useLocation();
  const isPharmacy = location.pathname.startsWith('/pharmacy');
  const [formData, setFormData] = useState<FormData>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [submittedGmailUrl, setSubmittedGmailUrl] = useState('');
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const validate = () => {
    const errs: Partial<FormData> = {};
    if (!formData.name.trim()) errs.name = 'Please enter your name';
    if (!formData.email.trim()) {
      errs.email = 'Please enter your email';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errs.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      errs.phone = 'Please enter your phone number';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const to = 'kuldip@devdeepsystems.com';

    const customerIdentifier = formData.name.trim()
      ? `${formData.name.trim()}${formData.hospital?.trim() ? ` (${formData.hospital.trim()})` : ''}`
      : 'Prospective Client';
    const emailSubject = `Enquiry regarding ${formData.module} - ${customerIdentifier}`;
    const subject = encodeURIComponent(emailSubject);

    const organizationLabel = isPharmacy ? 'Clinic / Store Name' : 'Hospital / Organization Name';
    const contactDetails = [
      `Name: ${formData.name}`,
      `${organizationLabel}: ${formData.hospital || 'N/A'}`,
      `Email: ${formData.email}`,
      `Phone: ${formData.phone}`,
    ].join('\n');

    const fullBody = formData.message.trim()
      ? `${formData.message.trim()}\n\n---\n${contactDetails}`
      : contactDetails;

    const body = encodeURIComponent(fullBody);
    const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${to}&su=${subject}&body=${body}`;

    setSubmittedGmailUrl(gmailUrl);
    setSubmitted(true);

    const win = window.open(gmailUrl, '_blank');
    if (!win) {
      window.location.href = gmailUrl;
    }
  };

  return (
    <section
      id="contact"
      className="py-20 md:py-28 bg-slate-50 border-t border-slate-200 relative overflow-hidden"
    >
      {/* Background accents */}
      <div className="absolute top-1/2 left-0 w-80 h-80 bg-[#0D7893]/10 blur-3xl rounded-full pointer-events-none" />

      <Container>
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <ScrollReveal direction="down">
            <span className="inline-flex items-center gap-1.5 px-3.5 py-1 rounded-full bg-[#0D7893]/10 text-[#0D7893] text-xs font-bold uppercase tracking-wider border border-[#0D7893]/20">
              <Sparkles className="w-3.5 h-3.5" /> Get in Touch
            </span>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={100}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
              Ready to Modernize Your {isPharmacy ? 'Pharmacy' : 'Hospital'} Operations?
            </h2>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={200}>
            <p className="text-slate-600 text-base md:text-lg">
              Schedule a personalized walkthrough of Divine {isPharmacy ? 'Pharmacy' : 'HMS'} with
              our healthcare technology specialists or discuss custom modules for your{' '}
              {isPharmacy ? 'store' : 'hospital'}.
            </p>
          </ScrollReveal>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start max-w-6xl mx-auto">
          {/* Left Column: Direct Contact Info */}
          <div className="lg:col-span-5 space-y-6">
            <ScrollReveal direction="left" delay={200}>
              <div className="p-8 rounded-3xl bg-white border border-slate-200 shadow-xl shadow-slate-900/5 space-y-8">
                <div>
                  <h3 className="text-xl font-bold text-slate-900">Devdeep Systems Pvt. Ltd.</h3>
                  <p className="text-sm text-slate-500 mt-1">
                    Divine HMS Division · Healthcare Software Engineering
                  </p>
                </div>

                <div className="space-y-6">
                  {/* Phone */}
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-[#0D7893]/10 text-[#0D7893] flex-shrink-0">
                      <Phone className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase text-slate-400 tracking-wider">
                        Call or WhatsApp
                      </div>
                      <div className="text-slate-800 font-semibold text-sm sm:text-base mt-0.5 space-y-1">
                        <div>
                          <a
                            href="tel:9752002111"
                            className="hover:text-[#0D7893] transition-colors"
                          >
                            +91 9752002111
                          </a>
                        </div>
                        <div>
                          <a
                            href="tel:+917714337129"
                            className="hover:text-[#0D7893] transition-colors"
                          >
                            +91 7714337129
                          </a>
                        </div>
                        <div>
                          <a
                            href="tel:7987702243"
                            className="hover:text-[#0D7893] transition-colors"
                          >
                            +91 7987702243
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Email */}
                  <div className="flex items-start gap-4">
                    <div className="p-3 rounded-xl bg-[#0D7893]/10 text-[#0D7893] flex-shrink-0">
                      <Mail className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-xs font-bold uppercase text-slate-400 tracking-wider">
                        Email Inquiry
                      </div>
                      <a
                        href="mailto:kuldip@devdeepsystems.com"
                        className="text-slate-800 font-semibold text-sm sm:text-base mt-0.5 block hover:text-[#0D7893] transition-colors"
                      >
                        kuldip@devdeepsystems.com
                      </a>
                    </div>
                  </div>

                  {/* Address */}
                  <div className="space-y-3">
                    <div className="flex items-start gap-4">
                      <div className="p-3 rounded-xl bg-[#0D7893]/10 text-[#0D7893] flex-shrink-0">
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <div className="text-xs font-bold uppercase text-slate-400 tracking-wider">
                          Development Headquarters
                        </div>
                        <p className="text-slate-700 text-sm mt-0.5 leading-relaxed">
                          Floor no. 2, Q.N.02, Street no 03, Krishna Nagar, i/F Pooja plastic
                          factory, Devpuri, Raipur, Chhattisgarh, India
                        </p>
                      </div>
                    </div>

                    {/* Google Map Tile */}
                    <div className="relative rounded-2xl overflow-hidden border border-slate-200/90 shadow-sm bg-slate-100 group">
                      <iframe
                        title="Devdeep Systems Office Location Map"
                        src="https://maps.google.com/maps?q=21.2120475,81.6691763&hl=en&z=16&output=embed"
                        width="100%"
                        height="190"
                        style={{ border: 0 }}
                        allowFullScreen={false}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        className="w-full h-48 block"
                      />
                      <a
                        href="https://www.google.com/maps?q=21.2120475,81.6691763"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="absolute bottom-2.5 right-2.5 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/95 backdrop-blur-sm text-xs font-semibold text-[#0D7893] shadow-md border border-slate-200/80 hover:bg-[#0D7893] hover:text-white transition-all duration-200"
                      >
                        <span>Open in Google Maps</span>
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-[#0D7893]/5 border border-[#0D7893]/20">
                  <div className="flex items-center gap-2 text-xs font-bold text-[#0D7893] uppercase tracking-wider">
                    <Building2 className="w-4 h-4" /> Implementation Support
                  </div>
                  <p className="text-xs text-slate-600 mt-1">
                    Staff role training, and data migration assistance included.
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>

          {/* Right Column: Demo Request Form */}
          <div className="lg:col-span-7">
            <ScrollReveal direction="right" delay={200}>
              <div className="p-8 sm:p-10 rounded-3xl bg-white border border-slate-200 shadow-xl shadow-slate-900/5 relative">
                {submitted ? (
                  <div className="py-12 text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 mx-auto flex items-center justify-center">
                      <CheckCircle2 className="w-10 h-10" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900">
                      Thanks for contacting, we'll get back to you soon
                    </h3>
                    <p className="text-slate-600 max-w-md mx-auto text-sm sm:text-base">
                      Your inquiry has been prepared in Gmail. If the compose window did not open automatically,{' '}
                      <a
                        href={submittedGmailUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#0D7893] underline font-semibold hover:text-[#0A6278]"
                      >
                        click here to open Gmail
                      </a>
                      .
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setSubmitted(false);
                        setFormData(initialForm);
                      }}
                      className="mt-4"
                    >
                      Submit Another Inquiry
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-5" noValidate>
                    <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                      <div>
                        <h3 className="text-xl font-bold text-slate-900">
                          Request a Live Walkthrough
                        </h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                          {isPharmacy
                            ? 'Experience Divine Pharmacy configured for your Clinic'
                            : 'Experience Divine HMS configured for your hospital size'}
                        </p>
                      </div>
                      <MessageSquare className="w-5 h-5 text-[#0D7893]" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Name */}
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Dr. Rajesh Sharma"
                          className={`w-full px-4 py-3 rounded-xl border text-sm text-slate-800 placeholder-slate-400 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0D7893] transition-all ${
                            errors.name ? 'border-rose-400 bg-rose-50/50' : 'border-slate-200'
                          }`}
                        />
                        {errors.name && (
                          <span className="text-xs text-rose-500 mt-1 block">{errors.name}</span>
                        )}
                      </div>

                      {/* Hospital / Organization */}
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                          {isPharmacy ? 'Clinic/Store' : 'Hospital'} Name
                        </label>
                        <input
                          type="text"
                          name="hospital"
                          value={formData.hospital}
                          onChange={handleChange}
                          placeholder={
                            isPharmacy ? 'Pharmacy store' : 'City Multi-Specialty Hospital'
                          }
                          className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-800 placeholder-slate-400 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0D7893] transition-all"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Email */}
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                          Email Address *
                        </label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder={isPharmacy ? 'owner@store.com' : 'director@hospital.com'}
                          className={`w-full px-4 py-3 rounded-xl border text-sm text-slate-800 placeholder-slate-400 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0D7893] transition-all ${
                            errors.email ? 'border-rose-400 bg-rose-50/50' : 'border-slate-200'
                          }`}
                        />
                        {errors.email && (
                          <span className="text-xs text-rose-500 mt-1 block">{errors.email}</span>
                        )}
                      </div>

                      {/* Phone */}
                      <div>
                        <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+91 98765 43210"
                          className={`w-full px-4 py-3 rounded-xl border text-sm text-slate-800 placeholder-slate-400 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0D7893] transition-all ${
                            errors.phone ? 'border-rose-400 bg-rose-50/50' : 'border-slate-200'
                          }`}
                        />
                        {errors.phone && (
                          <span className="text-xs text-rose-500 mt-1 block">{errors.phone}</span>
                        )}
                      </div>
                    </div>

                    {/* Primary Module of Interest */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                        Module of Primary Interest
                      </label>
                      <select
                        name="module"
                        value={formData.module}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-800 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0D7893] transition-all"
                      >
                        {isPharmacy ? (
                          <>
                            <option value="All Pharmacy Modules (Pharma ERP)">
                              All Pharmacy Modules (Pharma ERP)
                            </option>
                            <option value="POS Dispensing & Counter Billing">
                              POS Dispensing & Counter Billing
                            </option>
                            <option value="Stock Inventory & Batch Expiry Tracking">
                              Stock Inventory & Batch Expiry Tracking
                            </option>
                            <option value="Inward Stock (GRN) & Purchase Orders">
                              Inward Stock (GRN) & Purchase Orders
                            </option>
                            <option value="Medicine Returns & Supplier Credit Notes">
                              Medicine Returns & Supplier Credit Notes
                            </option>
                            <option value="Master Catalog & Drug Compliance Setup">
                              Master Catalog & Drug Compliance Setup
                            </option>
                            <option value="Chemist Store Setup">
                              Chemist Store / Medical Shop Setup
                            </option>
                            <option value="Multi-Branch Pharmacy Chain Setup">
                              Multi-Branch Pharmacy Chain Setup
                            </option>
                          </>
                        ) : (
                          <>
                            <option value="All Modules (Complete ERP)">
                              All Modules (Complete Hospital ERP Suite)
                            </option>
                            <option value="OPD & Fast Billing">
                              OPD (Outpatient Department) & Fast Billing
                            </option>
                            <option value="IPD & Ward Operations">
                              IPD (Inpatient Department) & Ward Operations
                            </option>
                            <option value="Bed Management (ATD)">
                              Bed Management (Allocate / Transfer / Discharge)
                            </option>
                            <option value="Appointments & Doctor Scheduling">
                              Appointments & Doctor Scheduling
                            </option>
                            <option value="Discharge Summaries & Reports">
                              Discharge Summaries & Analytics Reports
                            </option>
                          </>
                        )}
                      </select>
                    </div>

                    {/* Message / Requirements */}
                    <div>
                      <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                        {isPharmacy
                          ? 'Pharmacy Store Requirements / Questions'
                          : 'Hospital Requirements / Questions'}
                      </label>
                      <textarea
                        name="message"
                        rows={3}
                        value={formData.message}
                        onChange={handleChange}
                        placeholder={
                          isPharmacy
                            ? 'Tell us about your pharmacy current systems, requirments or specific workflow needs...'
                            : 'Tell us about your hospital bed capacity, current system, or specific workflow needs...'
                        }
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-slate-800 placeholder-slate-400 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#0D7893] transition-all"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full gap-2 shadow-lg shadow-[#0D7893]/20"
                    >
                      <Send className="w-4 h-4" />
                      <span>Book Guided Live Demo</span>
                    </Button>
                  </form>
                )}
              </div>
            </ScrollReveal>
          </div>
        </div>
      </Container>
    </section>
  );
};

export default Contact;
